import apiClient from './api-client';
import { Purpose } from "./purpose-service";

// API Path Constants
const API_BASE = '/masterdata/processors';
const API_PATHS = {
    BASE: API_BASE,
    BY_ID: (id: number) => `${API_BASE}/${id}`,
    TRANSLATIONS: (processorId: number) => `${API_BASE}/${processorId}/translations`,
    TRANSLATION: (processorId: number, languageCode: string) => `${API_BASE}/${processorId}/translations/${languageCode}`,
    PUBLISH: (processorId: number) => `${API_BASE}/${processorId}/publish`,
    UNPUBLISH: (processorId: number) => `${API_BASE}/${processorId}/unpublish`,
} as const;

export type ProcessorType = 'PROCESSOR' | 'FIDUCIARY';

export interface ThirdPartyProcessorTranslation {
    translationId: number;
    processorId: number;
    languageCode: string;
    translatedName: string;
    translatedDescription?: string;
    isActive: boolean;
    createdDate: Date;
    createdBy: number;
    modifiedDate?: Date;
    modifiedBy?: number;
}

export interface ThirdPartyProcessor {
    processorId?: number;
    name: string;
    description?: string;
    type: ProcessorType;
    website?: string;
    contactEmail?: string;
    contactPhone?: string;
    dataProcessingAgreementUrl?: string;
    isPublished: boolean;
    createdDate?: Date;
    createdBy?: number;
    modifiedDate?: Date;
    modifiedBy?: number;
    translations?: ThirdPartyProcessorTranslation[];
}

export interface ThirdPartyProcessorQueryParams {
    filters?: {
        isPublished?: boolean;
        type?: ProcessorType;
    };
    sortBy?: 'a-z' | 'z-a' | 'newest' | 'oldest';
    pagination?: {
        page: number;
        pageSize: number;
    };
}

export interface PaginatedResponse<T> {
    processors: T[];
    total: number;
}

export interface PurposeMapping {
    processorId: number;
    processorName: string;
    purposes: Purpose[];
}

// Service methods
export const tppService = {
    getProcessors: async (queryParams?: ThirdPartyProcessorQueryParams): Promise<PaginatedResponse<ThirdPartyProcessor>> => {
        const queryParamsObj = new URLSearchParams();
        
        if (queryParams?.filters) {
            if (queryParams.filters.isPublished !== undefined) {
                queryParamsObj.append('isPublished', queryParams.filters.isPublished.toString());
            }
            if (queryParams.filters.type) {
                queryParamsObj.append('type', queryParams.filters.type);
            }
        }
        if (queryParams?.sortBy) {
            queryParamsObj.append('sortBy', queryParams.sortBy);
        }
        if (queryParams?.pagination) {
            queryParamsObj.append('page', queryParams.pagination.page.toString());
            queryParamsObj.append('pageSize', queryParams.pagination.pageSize.toString());
        }

        const response = await apiClient.get<{ 
            success: boolean; 
            data: ThirdPartyProcessor[]; 
            pagination: {
                total: number;
                page: number;
                pageSize: number;
                totalPages: number;
            }
        }>(`${API_PATHS.BASE}?${queryParamsObj.toString()}`);
        
        return {
            processors: response.data,
            total: response.pagination.total
        };
    },

    getProcessorById: async (id: number): Promise<ThirdPartyProcessor | undefined> => {
        const response = await apiClient.get<{ success: boolean; data: any }>(API_PATHS.BY_ID(id));
        
        // Map the response data to match our interface
        const processorData = response.data;
        
        // Map translations if they exist
        let mappedTranslations: ThirdPartyProcessorTranslation[] | undefined;
        if (processorData.translations && Array.isArray(processorData.translations)) {
            mappedTranslations = processorData.translations.map((translation: any) => ({
                translationId: translation.translationId || 0,
                processorId: translation.processorId || id,
                languageCode: translation.LanguageCode || translation.languageCode,
                translatedName: translation.TranslatedName || translation.translatedName,
                translatedDescription: translation.TranslatedDescription || translation.translatedDescription,
                isActive: translation.isActive !== undefined ? translation.isActive : true,
                createdDate: translation.createdDate ? new Date(translation.createdDate) : new Date(),
                createdBy: translation.createdBy || 0,
                modifiedDate: translation.modifiedDate ? new Date(translation.modifiedDate) : undefined,
                modifiedBy: translation.modifiedBy
            }));
        }
        
        return {
            processorId: processorData.processorId,
            name: processorData.name,
            description: processorData.description,
            type: processorData.type,
            website: processorData.website,
            contactEmail: processorData.contactEmail,
            contactPhone: processorData.contactPhone,
            dataProcessingAgreementUrl: processorData.dataProcessingAgreementUrl,
            isPublished: processorData.isPublished,
            createdDate: processorData.createdDate ? new Date(processorData.createdDate) : undefined,
            createdBy: processorData.createdBy,
            modifiedDate: processorData.modifiedDate ? new Date(processorData.modifiedDate) : undefined,
            modifiedBy: processorData.modifiedBy,
            translations: mappedTranslations
        };
    },

    createProcessor: async (processor: Omit<ThirdPartyProcessor, 'processorId' | 'createdDate' | 'createdBy' | 'modifiedDate' | 'modifiedBy'>): Promise<ThirdPartyProcessor> => {
        const response = await apiClient.post<{ success: boolean; data: ThirdPartyProcessor }>(API_PATHS.BASE, processor);
        return response.data;
    },

    updateProcessor: async (id: number, updates: Partial<ThirdPartyProcessor>): Promise<ThirdPartyProcessor> => {
        const response = await apiClient.put<{ success: boolean; data: ThirdPartyProcessor }>(API_PATHS.BY_ID(id), updates);
        return response.data;
    },

    publishProcessor: async (id: number): Promise<ThirdPartyProcessor> => {
        const response = await apiClient.patch<{ success: boolean; data: ThirdPartyProcessor }>(
            API_PATHS.PUBLISH(id),
            { isPublished: true }
        );
        return response.data;
    },

    unpublishProcessor: async (id: number): Promise<ThirdPartyProcessor> => {
        const response = await apiClient.patch<{ success: boolean; data: ThirdPartyProcessor }>(
            API_PATHS.UNPUBLISH(id),
            { isPublished: false }
        );
        return response.data;
    },

    updateTranslation: async (processorId: number, translation: Omit<ThirdPartyProcessorTranslation, 'translationId' | 'createdDate' | 'createdBy'>): Promise<ThirdPartyProcessorTranslation> => {
        const payload = {
            languageCode: translation.languageCode,
            translatedName: translation.translatedName,
            translatedDescription: translation.translatedDescription
        };
        
        const response = await apiClient.put<{ success: boolean; data: ThirdPartyProcessorTranslation }>(
            `${API_PATHS.BASE}/${processorId}/translation`,
            payload
        );
        return response.data;
    },

    deleteProcessor: async (id: number): Promise<void> => {
        await apiClient.delete<{ success: boolean; message: string }>(API_PATHS.BY_ID(id));
    },

    getPurposeMappingsForProcessor: async (processorId: number): Promise<PurposeMapping | undefined> => {
        // Implementation needed
        throw new Error('Method not implemented');
    }
}; 